import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/data/status-badge";
import { Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { ItemPublicoDTO } from "@shared/mercado-comprador";

/**
 * `/p/:token` — o que o comprador registrou PARA MIM, para o produtor SEM conta.
 *
 * É a outra metade do que faz a carga fechar: a maioria dos produtores da praça
 * não tem app, e o comprador anota o que combinou com eles. Este link é o que
 * transforma a anotação num documento que as duas partes leem.
 *
 * ⚠️ O PRODUTOR NÃO VÊ A CARGA. Nem o identificador dela, nem os outros
 * produtores, nem o destino, nem o preço de venda, nem a margem. Ele vê o que é
 * DELE — e a data do carregamento, que é como ele reconhece de que dia se trata.
 *
 * ⚠️ Conferir NÃO altera o registro. Registra a contagem do outro lado; a
 * plataforma registra a divergência, não a arbitra. Mesma decisão da conferência
 * do comprador (C3), do outro lado do balcão.
 */

function fmtMoeda(v: number) {
  return `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtKg(v: number) {
  return `${v.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kg`;
}
function fmtData(iso: string) {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}
function rotulo(p: { variedade: string; qualidade: string | null }) {
  const v = p.variedade.charAt(0) + p.variedade.substring(1).toLowerCase();
  return p.qualidade ? `${v} ${p.qualidade}ª` : v;
}

export default function ItemPublicoPage() {
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [diferente, setDiferente] = useState(false);
  const [observacao, setObservacao] = useState("");

  const chave = ["/api/publico/item", token];
  const { data, isLoading, isError } = useQuery<ItemPublicoDTO>({
    queryKey: chave,
    queryFn: () => fetchJson<ItemPublicoDTO>(`/api/publico/item/${token}`),
    enabled: !!token,
  });

  const conferir = useMutation({
    mutationFn: (corpo: { bate: boolean; observacao?: string | null }) =>
      apiRequest("POST", `/api/publico/item/${token}/conferir`, corpo).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chave });
      setDiferente(false);
      toast({ title: "Obrigado", description: "O comprador vai ver a sua resposta." });
    },
    // Nunca engolir: sem isto o produtor toca em "Bate" e nada acontece.
    onError: (e: Error) => toast({ title: "Não consegui registrar", description: e.message, variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-sm text-muted-foreground">
        Abrindo…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="font-bold">Este link não está mais disponível.</p>
        <p className="text-sm text-muted-foreground max-w-xs">
          Peça um link novo para quem registrou a carga.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto w-full max-w-[480px] bg-background min-h-screen">
        <header className="bg-[#111827] text-white px-4 py-5 space-y-1">
          <h1 className="text-base font-bold">
            {data.dataCarregamento ? `Carga de ${fmtData(data.dataCarregamento)}` : "Carga"}
          </h1>
          <p className="text-xs text-white/70">registrada por {data.comprador.nome}</p>
        </header>

        <div className="p-4 space-y-5">
          <p className="text-sm">
            <span className="text-muted-foreground">Produtor: </span>
            <b>{data.produtor.nome}</b>
          </p>

          <section className="space-y-1">
            <h2 className="text-xs uppercase tracking-wide text-muted-foreground">O que foi carregado</h2>
            <ul className="divide-y">
              {data.itens.map((item, i) => (
                <li key={i} className="py-2 flex justify-between gap-2 text-sm" data-testid={`item-publico-${i}`}>
                  <span className="min-w-0">
                    <span className="block">{rotulo(item)}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {fmtKg(item.kg)} × {fmtMoeda(item.precoKg)}
                    </span>
                  </span>
                  <span className="tabular-nums shrink-0">{fmtMoeda(item.kg * item.precoKg)}</span>
                </li>
              ))}
            </ul>
          </section>

          <div className="rounded-lg bg-[#111827] text-white px-4 py-3 space-y-1">
            <div className="flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-wide">Total</span>
              <span className="text-xl font-bold tabular-nums">{fmtMoeda(data.totalValor)}</span>
            </div>
            <p className="text-xs text-white/70 tabular-nums">{fmtKg(data.totalKg)}</p>
          </div>

          {data.vencimento && (
            <p className="text-sm">
              <span className="text-muted-foreground">Vencimento: </span>
              <b>{fmtData(data.vencimento)}</b>
            </p>
          )}

          {/* ── Conferir ── */}
          <section className="space-y-3 rounded-lg border p-3">
            {data.conferidoEm ? (
              <div className="flex items-center gap-2">
                <StatusBadge tom="sucesso" ponto>Você já conferiu</StatusBadge>
                {/* Conferir de novo ATUALIZA: o produtor recontou, e o histórico
                    de quem disse o quê está nos eventos, que são append-only. */}
                <Button variant="ghost" size="sm" onClick={() => setDiferente(true)}>
                  Está diferente?
                </Button>
              </div>
            ) : (
              <h2 className="font-medium">Confira os números</h2>
            )}

            {!diferente ? (
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  onClick={() => conferir.mutate({ bate: true })}
                  disabled={conferir.isPending}
                  data-testid="button-bate"
                >
                  <Check className="h-4 w-4 mr-1" />Bate
                </Button>
                <Button
                  variant="outline" className="flex-1"
                  onClick={() => setDiferente(true)}
                  data-testid="button-esta-diferente"
                >
                  Está diferente
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="obs-conferencia">O que está diferente?</Label>
                <Textarea
                  id="obs-conferencia" value={observacao} rows={3}
                  onChange={e => setObservacao(e.target.value)}
                  placeholder="Ex.: foram 4.500 kg de primeira, não 4.300."
                  data-testid="input-observacao-conferencia"
                />
                {/* O motivo é obrigatório e o servidor recusa sem ele: uma
                    divergência sem texto vira pendência que o comprador não sabe
                    resolver. */}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setDiferente(false)}>
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={() => conferir.mutate({ bate: false, observacao })}
                    disabled={!observacao.trim() || conferir.isPending}
                    data-testid="button-enviar-divergencia"
                  >
                    Enviar
                  </Button>
                </div>
              </div>
            )}
          </section>

          <div className="pt-2 border-t space-y-3">
            <p className="text-xs text-muted-foreground">
              Registro feito por {data.comprador.nome} no Rizoma. Este link é pessoal.
            </p>
            <Button
              className="w-full"
              onClick={() => setLocation(`/p/${token}/criar-acesso`)}
              data-testid="button-criar-conta-produtor"
            >
              Criar minha conta e acompanhar minhas vendas
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
